import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { axialToWorld } from '@/game/hexmap';
import config from '@/game/hexmap/config';
import { watchedTiles } from '@/game/sim';
import { colorIndexFor, PLAYER_COLORS } from '@/game/view/palette';
import { useSimFrame } from './app/simFrame';
import { placeMatrix, useModelBatcher, WATCH_SPOT_LOOK } from './ModelBatcher';

const GROUND_Y = config.hexMap.tile.thickness;
const LINE_WIDTH = 0.06;
const LINE_OPACITY = 0.45;
const lineGeometry = new THREE.PlaneGeometry(1, 1);
lineGeometry.rotateX(-Math.PI / 2);
const noRaycast = () => null;
const scratchMatrix = new THREE.Matrix4();

interface WatchSpotsLayerProps {
  /** A selected tower also gets a faint line out to its spot. */
  selectedBuildingId: string | null;
}

/**
 * The player's own watchtowers' watch spots (`Building.watch`), only while
 * watched (the tower completed and the spot within its reach): an eye marker
 * on the spot (through the shared `ModelBatcher`), and a faint line from the
 * selected tower to it. Owner only: rivals' spots are never drawn.
 */
export function WatchSpotsLayer({ selectedBuildingId }: WatchSpotsLayerProps) {
  const { view: state, playerId, playerOrder, rules } = useSimFrame();
  const color = PLAYER_COLORS[colorIndexFor(playerId, playerOrder)];
  const line = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: LINE_OPACITY,
        depthWrite: false,
      }),
    [color]
  );
  const batcher = useModelBatcher();
  /** Entity ids this layer has in the batcher. */
  const placed = useRef(new Set<string>());
  const towers = Object.values(state.buildings).filter(
    (b) => b.owner === playerId && b.watch && watchedTiles(rules, b).length > 0
  );

  useLayoutEffect(() => {
    const keep = new Set<string>();
    for (const b of towers) {
      if (!b.watch) continue;
      const id = `w:${b.id}`;
      keep.add(id);
      const to = axialToWorld(b.watch, config.hexMap.tile.radius);
      batcher.set(id, WATCH_SPOT_LOOK, placeMatrix(scratchMatrix, to.x, GROUND_Y, to.z), color);
      placed.current.add(id);
    }
    for (const id of placed.current) {
      if (keep.has(id)) continue;
      batcher.remove(id);
      placed.current.delete(id);
    }
  });
  useEffect(
    () => () => {
      for (const id of placed.current) batcher.remove(id);
      placed.current.clear();
    },
    [batcher]
  );

  return (
    <>
      {towers.map((b) => {
        if (!b.watch) return null;
        const from = axialToWorld(b.pos, config.hexMap.tile.radius);
        const to = axialToWorld(b.watch, config.hexMap.tile.radius);
        const dx = to.x - from.x;
        const dz = to.z - from.z;
        const length = Math.hypot(dx, dz);
        return (
          <group key={b.id}>
            {b.id === selectedBuildingId && length > 0 && (
              <mesh
                geometry={lineGeometry}
                material={line}
                raycast={noRaycast}
                position={[(from.x + to.x) / 2, GROUND_Y + 0.03, (from.z + to.z) / 2]}
                rotation={[0, -Math.atan2(dz, dx), 0]}
                scale={[length, 1, LINE_WIDTH]}
              />
            )}
          </group>
        );
      })}
    </>
  );
}
